import {Directive, inject, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {FormGroup, FormGroupDirective, NgForm} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
  DynamicFormControl,
  DynamicFormGroup,
  ResourceSelectComponent
} from '@q9elements/ui-kit/changes/shared';
import {BaseDialogComponent, ChangesType, SessionService} from '@q9elements/ui-kit/common';
import {TagsSelectComponent} from '@q9elements/ui-kit/components';
import {Observable} from 'rxjs';

import {ChangesBulkOperationsDaoService} from '../../services/bulk-operations-dao.service';
import {BulkCustomFieldsFormComponent} from '../bulk-custom-fields-form/bulk-custom-fields-form.component';

@Directive()
export abstract class ChangesBulkEditDialogComponent
  extends BaseDialogComponent
  implements ErrorStateMatcher
{
  @ViewChild(TagsSelectComponent) tagSelect: TagsSelectComponent;
  @ViewChild(ResourceSelectComponent) resourceSelect: ResourceSelectComponent;
  @ViewChildren(BulkCustomFieldsFormComponent)
  customFieldsSelects: QueryList<BulkCustomFieldsFormComponent>;

  abstract readonly itemType: ChangesType;

  public isPreview = false;
  public isLoading = false;
  public bulkEditForm = new DynamicFormGroup({
    release: new DynamicFormControl(),
    devProject: new DynamicFormControl(),
    assignee: new DynamicFormControl(),
    tags: new DynamicFormControl(false, []),
    customFields: new DynamicFormGroup({})
  });
  public uiFormGroup = this.bulkEditForm as FormGroup;

  protected sessionService = inject(SessionService);
  protected teamId = this.sessionService.teamId();
  protected daoService = inject(ChangesBulkOperationsDaoService);
  protected items = inject<string[]>(MAT_DIALOG_DATA);
  protected override dialogRef = inject(MatDialogRef<ChangesBulkEditDialogComponent>);

  public toPreview() {
    const {invalid, includedFields} = this.bulkEditForm;

    if (!includedFields) {
      this.error = 'BULK.NOT_SELECTED';

      return;
    }

    if (invalid) {
      return;
    }

    this.togglePreviewMode();
  }

  public togglePreviewMode() {
    this.error = '';

    this.isPreview = !this.isPreview;

    if (this.isPreview) {
      this.clearFieldsSearch();

      this.bulkEditForm.disable({emitEvent: false});
    } else {
      this.bulkEditForm.enable({emitEvent: false});
    }
  }

  protected sendSubmitRequest<T>(): Observable<unknown | T> {
    const items = this.items;
    const payload = this.bulkEditForm.includedFields;

    this.error = '';
    this.isLoading = true;

    return this.daoService.updateFields(this.itemType, {items, payload});
  }

  public isErrorState(
    control: DynamicFormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return !!(control?.included && control?.invalid && form?.submitted);
  }

  private clearFieldsSearch(): void {
    const includedFields = this.bulkEditForm.includedFields;
    const {assignee} = this.bulkEditForm.controls;

    if (!assignee.value) {
      assignee.setValue(null, {emitEvent: false});
    }

    if (typeof includedFields === 'object') {
      const actions: Record<string, () => void> = {
        affectedRoles: () => this.resourceSelect.chipsSelect.resetSearchInput(),
        customFields: () =>
          this.customFieldsSelects.forEach(customField => {
            if (customField.chipsSelect) {
              customField.chipsSelect.resetSearchInput();
            }
          }),
        tags: () => this.tagSelect.chipsSelect.resetSearchInput()
      };

      Object.keys(actions).forEach(key => {
        if (includedFields[key]) {
          actions[key]();
        }
      });
    }
  }
}
